@if (state==='inline-compact') {
  <div class="inline-compact-wrapper">
    @if (!hideLabel && !loading) {
      <span class="inline-compact-label">{{ getInlineCompactLabel() }}</span>
    }
    @if (!loading) {
      <div class="header-nutrient-holder inline-compact">
        @for (nutrient of getDisplayInHeaderNutrients(); track nutrient.tag) {
          <div class="nutrient header circle-holder" [attr.nutrient-color]="getNutrientColor(nutrient)"
            [class.target-satisfied]="shouldShowGreenColor()">
            <div class="nutrient-name">
              @if (nutrient.icon) {
                <cs-icon [name]="nutrient.icon" class="nutrient-icon"></cs-icon>
              }
              @if (!nutrient.icon) {
                {{ getInlineCompactNutrientLabel(nutrient) }}
              }
            </div>
            <div class="nutrient-value">{{ nutrient.valueShort }}</div>
          </div>
        }
      </div>
    }
    @if (loading) {
      <cs-skeleton width="120px"></cs-skeleton>
    }
  </div>
}

@if (state==='linear-card') {
  <div class="linear-card-nutrient-list">
    <div class="linear-card-main">
      @if (getNutrientEnergy(); as caloriesNutrient) {
        <!-- Calories circular chart on the left -->
        <div class="calories-circle-container"
          [class.target-satisfied]="shouldShowGreenColor() && displayMode === NutrientDisplayMode.Logged">
          <div class="circle-chart" [class.remaining-mode]="displayMode === NutrientDisplayMode.Remaining">
            <svg viewBox="0 0 135 135" class="circle-svg">
              <!-- Background circle - outer radius 63px -->
              <circle r="63" cx="67.5" cy="67.5" fill="transparent" stroke-width="9" class="circle-background">
              </circle>
              <!-- Progress circle (capped at 100%) - outer radius 63px -->
              <circle r="63" cx="67.5" cy="67.5" fill="transparent" stroke-width="9" class="circle-progress"
              [attr.stroke-dasharray]="'396'" [attr.stroke-dashoffset]="displayMode === NutrientDisplayMode.Logged
                      ? 396 - (396 * getCircularProgressPercentage(caloriesNutrient) / 100)
                      : 396 - (396 * (100 - getCircularProgressPercentage(caloriesNutrient)) / 100)">
              </circle>
              @if (isNutrientOverTarget(caloriesNutrient)) {
                <!-- Overage circle (only when over target) - inner radius 50px -->
                <circle r="50" cx="67.5" cy="67.5" fill="transparent"
                  stroke-width="9" class="circle-overage" [attr.stroke-dasharray]="'314'"
                  [attr.stroke-dashoffset]="314 - (314 * getNutrientOveragePercentage(caloriesNutrient) / 100)">
                </circle>
              }
            </svg>
            <div class="circle-content">
              <div class="calories-value">
                <cs-icon [name]="isNutrientTargetSatisfied(caloriesNutrient) ? 'checkmark-circle' : 'flame'"
                class="calories-icon"></cs-icon>
                <span class="value">{{ Math.round(getNutrientDisplayValue(caloriesNutrient)) }}</span>
              </div>
              <div class="calories-target">
                @if (displayMode === NutrientDisplayMode.Logged) {
                  <span>{{
                  getNutrientTargetSlashText(caloriesNutrient) }} </span>
                }
                {{ caloriesNutrient.valueUnit }}
              </div>
            </div>
          </div>
          @if (isNutrientOverTarget(caloriesNutrient) && displayMode === NutrientDisplayMode.Logged) {
            <!-- Overage text below the circle -->
            <div class="calories-overage"
              [class.placeholder]="!isNutrientOverTarget(caloriesNutrient) || displayMode !== NutrientDisplayMode.Logged">
              @if (isNutrientOverTarget(caloriesNutrient) && displayMode === NutrientDisplayMode.Logged) {
                +{{ Math.round(getNutrientOverageAmount(caloriesNutrient)) }} {{ caloriesNutrient.valueUnit }}
              }
              @if (!isNutrientOverTarget(caloriesNutrient) || displayMode !== NutrientDisplayMode.Logged) {
                &nbsp;
              }
            </div>
          }
        </div>
      }
      <div class="macros-grid">
        @for (nutrient of getTrackedNutrientsExEnergy(); track nutrient.tag) {
          <div class="macro-item"
            [attr.nutrient-color]="getNutrientColor(nutrient)"
            [class.target-satisfied]="shouldShowGreenColor() && displayMode === NutrientDisplayMode.Logged">
            <div class="macro-label">{{ getNutrientCardLabel(nutrient) }}</div>
            <div class="progress-container">
              @if (getProgressBarLayout(nutrient); as layout) {
                <div class="progress-bar">
                  <ng-container *ngTemplateOutlet="barSegments; context: { $implicit: layout }"></ng-container>
                </div>
                @if (layout.tickmark.exists) {
                  <!-- Tickmark -->
                  <div class="tickmark" [style.left.%]="layout.tickmark.position_pct">
                  </div>
                }
              }
              <div class="macro-values">
                @if (displayMode === NutrientDisplayMode.Logged) {
                  @if (isNutrientTargetSatisfied(nutrient)) {
                    <cs-icon name="checkmark-circle"
                    class="satisfied-icon"></cs-icon>
                  }
                  {{ Math.round(nutrient.value || 0) }}{{ getNutrientTargetSlashText(nutrient) }}{{
                  nutrient.valueUnit }}{{ getNutrientMinMaxSuffix(nutrient) }}
                }
                @if (displayMode === NutrientDisplayMode.Remaining) {
                  {{
                  Math.round(getNutrientDisplayValue(nutrient)) }}{{ nutrient.valueUnit }}{{
                  getNutrientMinMaxSuffix(nutrient) }}
                }
              </div>
            </div>
          </div>
        }
      </div>
    </div>
    @if (expandable) {
      <!-- Nutrition Info link -->
      <cs-button tertiary icon-right icon="keyboard-arrow-right" (click)="handleNutritionInfoClick($event)">
        {{ LL.nutrition.nutritionInfo() }}
      </cs-button>
    }
  </div>
}

@if (state==='food-macros') {
  <div class="food-macros-container">
    <div class="food-macros-row">
      @if (getNutrientEnergy(); as caloriesNutrient) {
        <!-- Calories -->
        <div class="food-macro-item calories">
          <div class="macro-value-row">
            <cs-icon name="flame"></cs-icon>
            <span class="macro-value">{{ Math.round(caloriesNutrient.value || 0) }}</span>
          </div>
          <span class="macro-label">{{ caloriesNutrient.valueUnit }}</span>
        </div>
      }
      @if (getProteinNutrient(); as proteinNutrient) {
        <!-- Protein -->
        <div class="food-macro-item" [attr.nutrient-color]="'protein'">
          <span class="macro-value">{{ proteinNutrient.valueShort }}{{ proteinNutrient.valueUnit }}</span>
          <span class="macro-label">{{ LL.nutrientList.protein() }}</span>
        </div>
      }
      @if (getCarbNutrient(); as carbNutrient) {
        <!-- Carbs -->
        <div class="food-macro-item" [attr.nutrient-color]="'carbs'">
          <span class="macro-value">{{ carbNutrient.valueShort }}{{ carbNutrient.valueUnit }}</span>
          <span class="macro-label">{{ LL.nutrientList.carbs() }}</span>
        </div>
      }
      @if (getFatNutrient(); as fatNutrient) {
        <!-- Fat -->
        <div class="food-macro-item" [attr.nutrient-color]="'fat'">
          <span class="macro-value">{{ fatNutrient.valueShort }}{{ fatNutrient.valueUnit }}</span>
          <span class="macro-label">{{ LL.nutrientList.fat() }}</span>
        </div>
      }
      @if (getFiberNutrient(); as fiberNutrient) {
        <!-- Fiber -->
        <div class="food-macro-item" [attr.nutrient-color]="'fiber'">
          <span class="macro-value">{{ fiberNutrient.valueShort }}{{ fiberNutrient.valueUnit }}</span>
          <span class="macro-label">{{ LL.nutrientList.fiber() }}</span>
        </div>
      }
    </div>
    <div class="food-macros-progress-bar">
      @for (segment of getFoodMacrosBarSegments(); track segment.color) {
        <!-- Combined progress bar -->
        <div class="progress-segment" [attr.nutrient-color]="segment.color" [style.left.%]="segment.left_pct"
          [style.width.%]="segment.width_pct" [class.round-left]="segment.roundLeft"
          [class.round-right]="segment.roundRight">
        </div>
      }
    </div>
    @if (expandable) {
      <!-- Nutrients link -->
      <cs-button tertiary icon-right icon="keyboard-arrow-right" (click)="handleNutritionInfoClick($event)">
        {{ LL.nutrientList.nutrients() }}
      </cs-button>
    }
  </div>
}

@if (state==='compact-macros') {
  <!--
    Five fixed columns -- calories, protein, carbs, fat, fiber -- over these
    rows, in order: value, target, label, bar. Every row has a fixed height and
    every cell of a row that exists is emitted even when it has nothing to show,
    so the columns stay square whatever the data holds.

    The target row is the one thing that varies, and only between scopes: it is
    dropped on the food scope, where there are no targets, so the card is
    shorter there. Within one scope nothing moves -- switching Total to Left
    changes only the numbers. The grid contract is in
    design-docs/nutrient-bars.md.
  -->
  <div class="compact-macros-card">
    <div class="compact-macros-grid" [class.no-targets]="barStyle !== NutrientBarStyle.Targets">
      <!--
        The cells are emitted a ROW at a time, not a column at a time, because
        that is the order a CSS grid fills them in. A cell with nothing to show
        is still emitted: it holds its place so the rows stay square.
      -->
      @for (column of getCompactMacroColumns(); track column.colorKey) {
        <div class="compact-macro-value" [attr.nutrient-color]="column.colorKey">
          @if (column.nutrient; as nutrient) {
            {{ getCompactMacroValue(nutrient) }}
          }
        </div>
      }

      <!--
        The target row exists only where there are targets. On the food scope
        there are none, so the row is dropped entirely rather than left blank --
        a gap between a number and its own label reads as a mistake. That makes
        the card shorter on Food than on the meal scope, which is the one place
        this card deliberately changes height.
      -->
      @if (barStyle === NutrientBarStyle.Targets) {
        @for (column of getCompactMacroColumns(); track column.colorKey) {
          <div class="compact-macro-target">
            @if (column.nutrient; as nutrient) {
              {{ getCompactMacroTarget(nutrient) }}
            }
          </div>
        }
      }

      <!-- the label sits with its number, above the bar -->
      @for (column of getCompactMacroColumns(); track column.colorKey) {
        <div class="compact-macro-label">
          @if (column.nutrient; as nutrient) {
            {{ getLinearNutrientLabel(nutrient) }}
          }
        </div>
      }

      @if (barStyle === NutrientBarStyle.Targets) {
        <!-- one bar per macro, each measured against that macro's own target -->
        @for (column of getCompactMacroColumns(); track column.colorKey) {
          <div class="compact-macro-track" [attr.nutrient-color]="column.colorKey">
            <!--
              A macro with no target draws the empty grey track and nothing
              else. Without this guard the Remaining branch of
              getProgressBarLayout() would fill the whole track with a
              "still to come" bar, because with no target there is no tickmark
              to stop at -- and carbs commonly have no per-meal target, so that
              is the ordinary case, not an edge one.
            -->
            @if (column.nutrient; as nutrient) {
              @if (getNutrientTargetValue(nutrient) !== null && getProgressBarLayout(nutrient); as layout) {
                <!-- the overage turns green when the target passed was a floor, which is good news -->
                <ng-container *ngTemplateOutlet="barSegments; context: { $implicit: layout, pastFloor: isOveragePastFloor(nutrient) }"></ng-container>
                @if (layout.tickmark.exists) {
                  <div class="tickmark" [style.left.%]="layout.tickmark.position_pct"></div>
                }
              }
            }
          </div>
        }
      } @else {
        <!-- one bar across all five columns, split by how the calories divide -->
        <div class="compact-macros-distribution">
          @for (segment of getFoodMacrosBarSegments(); track segment.color) {
            <div class="progress-segment" [attr.nutrient-color]="segment.color" [style.left.%]="segment.left_pct"
              [style.width.%]="segment.width_pct" [class.round-left]="segment.roundLeft"
              [class.round-right]="segment.roundRight">
            </div>
          }
        </div>
      }
    </div>
    @if (expandable && showNutrientsLink) {
      <!-- Nutrients link -->
      <cs-button tertiary icon-right icon="keyboard-arrow-right" (click)="handleNutritionInfoClick($event)">
        {{ LL.nutrientList.nutrients() }}
      </cs-button>
    }
  </div>
}

@if (state==='linear') {
  <div class="linear-nutrient-list">
    @for (nutrient of getTrackedNutrients(); track trackByNutrientTag($index, nutrient)) {
      <div class="linear-nutrient"
        [attr.nutrient-color]="getNutrientColor(nutrient)" [class.is-calories]="nutrient.tag === INFOODS.Calories"
        [class.target-satisfied]="shouldShowGreenColor() && displayMode === NutrientDisplayMode.Logged">
        @if (nutrient.tag === INFOODS.Calories) {
          <div class="calories-header">
            @if (!isNutrientTargetSatisfied(nutrient) || displayMode !== NutrientDisplayMode.Logged) {
              <cs-icon
              name="flame" class="calories-icon"></cs-icon>
            }
            @if (isNutrientTargetSatisfied(nutrient) && displayMode === NutrientDisplayMode.Logged) {
              <cs-icon
              name="checkmark-circle" class="calories-icon satisfied-icon"></cs-icon>
            }
            <div class="nutrient-values above">
              {{ getLinearNutrientValueString(nutrient) }}
            </div>
          </div>
          <div class="progress-container full-width">
            @if (getProgressBarLayout(nutrient); as layout) {
              <div class="progress-bar">
                <ng-container *ngTemplateOutlet="barSegments; context: { $implicit: layout }"></ng-container>
              </div>
              @if (layout.tickmark.exists) {
                <!-- Tickmark -->
                <div class="tickmark" [style.left.%]="layout.tickmark.position_pct">
                </div>
              }
            }
          </div>
        }
        @if (nutrient.tag !== INFOODS.Calories) {
          <div class="nutrient-label">{{ getLinearNutrientLabel(nutrient) }}</div>
          <div class="progress-container">
            @if (getProgressBarLayout(nutrient); as layout) {
              <div class="progress-bar">
                <ng-container *ngTemplateOutlet="barSegments; context: { $implicit: layout }"></ng-container>
              </div>
              @if (layout.tickmark.exists) {
                <!-- Tickmark -->
                <div class="tickmark" [style.left.%]="layout.tickmark.position_pct">
                </div>
              }
            }
            <div class="nutrient-values below">
              @if (isNutrientTargetSatisfied(nutrient) && displayMode === NutrientDisplayMode.Logged) {
                <cs-icon
                name="checkmark-circle" class="satisfied-icon"></cs-icon>
                }{{ getLinearNutrientValueString(nutrient) }}
              </div>
            </div>
          }
        </div>
      }
      @if (expandable) {
        <!-- Nutrients link -->
        <cs-button tertiary icon-right icon="keyboard-arrow-right" (click)="handleNutritionInfoClick($event)">
          {{ LL.nutrientList.nutrients() }}
        </cs-button>
      }
    </div>
  }

<!-- one nutrient bar's segments, shared by every state; see design-docs/nutrient-bars.md -->
<ng-template #barSegments let-layout let-pastFloor="pastFloor">
  @if (layout.consumed.exists) {
    <!-- Consumed bar -->
    <div class="consumed-bar" [style.left.%]="layout.consumed.left_pct"
      [style.width.%]="layout.consumed.right_pct - layout.consumed.left_pct"
      [class.round-left]="layout.consumed.roundLeft" [class.round-right]="layout.consumed.roundRight">
    </div>
  }
  @if (layout.overage.exists) {
    <!-- Overage bar -->
    <div class="overage-bar" [class.past-floor]="pastFloor" [style.left.%]="layout.overage.left_pct"
      [style.width.%]="layout.overage.right_pct - layout.overage.left_pct"
      [class.round-left]="layout.overage.roundLeft" [class.round-right]="layout.overage.roundRight">
    </div>
  }
  @if (layout.remaining.exists) {
    <!-- Remaining bar -->
    <div class="remaining-bar" [style.left.%]="layout.remaining.left_pct"
      [style.width.%]="layout.remaining.right_pct - layout.remaining.left_pct"
      [class.round-left]="layout.remaining.roundLeft" [class.round-right]="layout.remaining.roundRight">
    </div>
  }
</ng-template>
